import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import dynamic from 'next/dynamic';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useMailBoxesOperations,
  useTeamsOperations,
  useToggleModal,
} from '@hooks';
import { generateShortId, showError } from '@utils';
import AttachmentList from 'components/emailTemplates/EmailTemplatesForm/AttachmentList/AttachmentList';
import { Label, InputFieldV2 } from 'components/common/form-controls';
import { ButtonV2 } from 'components/common/ui';
import { DEFAULT_MAILBOX } from 'hooks/communication/constants';

import {
  allowedFileTypes,
  MAX_FILE_SIZE,
  modules,
} from '../../communication/EmailReply/constants';
import { drawIconSvg } from '../../communication/EmailReply/EmailReply';
import EmailReplySignatures from '../../communication/EmailReply/EmailReplySignatures/EmailReplySignatures';
import EmailTemplatesModal from '../../communication/EmailTemplatesModal/EmailTemplatesModal';
import MailReceivers from './MailReceivers/MailReceivers';
import { styles } from './sendMailFields.style';
import { ModalFormSection } from '../modals/ModalV2';

const ReactQuill = dynamic(() => import('react-quill'), {
  ssr: false,
});

const SendMailFields = ({
  agent,
  isSubmitting,
  fromEmailAddress, // optional, if not provided, will default to the first mailbox or DEFAULT_MAILBOX
  showMailBoxSelector = false,
  onSendMail,
  onDelete,
  initialSubject = '',
  initialReceivers = [],
}) => {
  const { teams } = useTeamsOperations();
  const { mailBoxes } = useMailBoxesOperations();
  const [receivers, setReceivers] = useState(initialReceivers);
  const [ccReceivers, setCcReceivers] = useState([]);
  const [bccReceivers, setBccReceivers] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [subject, setSubject] = useState(initialSubject || '');
  const [attachments, setAttachments] = useState([]);
  const [editorValue, setEditorValue] = useState('');
  const [signature, setSignature] = useState(agent?.emailSignature);
  const mailBoxesOptions = mailBoxes?.map((item) => ({
    value: item.id,
    label: item.address,
  }));
  const selectedMailbox =
    mailBoxesOptions?.find(
      (mailbox) => mailbox?.label === (fromEmailAddress || DEFAULT_MAILBOX)
    ) ||
    mailBoxesOptions?.[0] ||
    null;
  const [from, setFrom] = useState(
    fromEmailAddress || selectedMailbox?.value || ''
  );
  const [signaturePopoverOpen, setSignaturePopoverOpen] = useState(false);
  const quillRef = useRef(null);
  const fileInputRef = useRef(null);

  const {
    isOpen: isTemplatesModalOpen,
    handleClose: handleCloseTemplatesModal,
    handleOpen: handleOpenTemplatesModal,
  } = useToggleModal(false);

  useEffect(() => {
    if (selectedMailbox) {
      setFrom(selectedMailbox.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const quillModules = useMemo(
    () => ({
      ...modules,
      toolbar: {
        container: modules.toolbar,
        handlers: {
          attachment: () => {
            fileInputRef.current?.click();
          },
          insertTemplate: () => {
            handleOpenTemplatesModal();
          },
          signatureAction: () => {
            // Open popover
            setSignaturePopoverOpen(true);
          },
        },
      },
    }),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let mounted = true;

    const registerIcon = async () => {
      const RQ = (await import('react-quill')).default;

      if (!mounted || !RQ?.Quill) return;

      const icons = RQ.Quill.import('ui/icons');
      icons.signatureAction = drawIconSvg;
    };

    registerIcon();

    return () => {
      mounted = false;
    };
  }, []);

  const handleClear = () => {
    setEditorValue('');
    setSignature(agent?.emailSignature);
    setSelectedTemplate(null);
    setAttachments([]);
    setCcReceivers([]);
    setBccReceivers([]);
    setReceivers([]);
    setSubject('');
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    const allowedExtensions = allowedFileTypes.accept
      .split(',')
      .map((ext) => ext.trim().toLowerCase().replace(/^\./, ''));

    const oversizedFiles = [];
    const unsupportedFiles = [];
    const validFiles = [];

    files.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        oversizedFiles.push(file);
        return;
      }

      const fileName = file.name.toLowerCase();
      const lastDotIndex = fileName.lastIndexOf('.');
      if (lastDotIndex === -1 || lastDotIndex === fileName.length - 1) {
        unsupportedFiles.push(file);
        return;
      }
      const extension = fileName.substring(lastDotIndex + 1);
      if (!allowedExtensions.includes(extension)) {
        unsupportedFiles.push(file);
        return;
      }
      validFiles.push(file);
    });

    if (oversizedFiles.length > 0) {
      showError(allowedFileTypes.sizeError);
      resetFileInput();
      return;
    }

    if (unsupportedFiles.length > 0) {
      showError(allowedFileTypes.typeError);
      resetFileInput();
      return;
    }

    const newAttachments = validFiles.map((file) => ({
      id: generateShortId(),
      name: file.name,
      size: file.size,
      type: file.type,
      file,
    }));

    setAttachments((prev) => [...prev, ...newAttachments]);
    resetFileInput();
  };

  const handleRemoveAttachment = (attachment) => {
    setAttachments((prev) => prev.filter((att) => att.id !== attachment.id));
  };

  const handleTextChange = (value) => {
    setEditorValue(value);
  };

  const handleChangeSignature = (newValue) => {
    if (newValue && quillRef.current) {
      setSignature(newValue.value);
      setSignaturePopoverOpen(false);
    }
  };

  const handleSend = async () => {
    if (!from) {
      showError('No mailbox selected');
      return;
    }
    if (!receivers?.length) {
      showError('Please add receivers');
      return;
    }

    try {
      const newAttachments = attachments
        .filter((attachment) => attachment.file)
        .map((attachment) => attachment.file);
      const attachmentIds = attachments
        .filter((attachment) => !attachment.file)
        .map((attachment) => attachment.id);
      const templateId = selectedTemplate?.template || null;
      const templateLang = selectedTemplate?.language || null;
      const body = `${editorValue} <br/><br/>${signature || ''}`
        // collapse multiple empty paragraphs
        .replace(/(<p><br><\/p>)+/g, '<br>')
        // keep real paragraphs intact
        .replace(/<p>(.*?)<\/p>/g, (match, content) => {
          if (!content.trim()) return '';
          return `<p>${content}</p>`;
        });
      const mailbox = mailBoxes?.find((mb) => mb.id === from);
      if (!mailbox) {
        showError('Selected mailbox not found');
        return;
      }
      await onSendMail({
        from: mailbox.address,
        body,
        subject,
        mailboxId: mailbox.id,
        templateId,
        templateLang,
        attachmentIds,
        attachments: newAttachments,
        to: receivers,
        cc: ccReceivers,
        bcc: bccReceivers,
      });

      handleClear();
    } catch (error) {
      showError(error);
    }
  };

  const handleInsertTemplateSubmit = (template = {}) => {
    setSelectedTemplate(template);
    setSubject(template.subject || '');
    handleCloseTemplatesModal();

    setEditorValue(template.body);
    setAttachments((prev) => [
      ...prev.filter((att) => att.file),
      ...template.attachments,
    ]);
  };

  const handleDelete = () => {
    handleClear();
    onDelete?.();
  };

  return (
    <ModalFormSection styles={{ padding: 0 }}>
      <Box sx={styles.container}>
        <Box sx={styles.containerInner}>
          {showMailBoxSelector && (
            <Box sx={styles.subjectWrapper}>
              <Label>From</Label>
              <InputFieldV2
                label=""
                type="dropdown"
                fullWidth
                options={mailBoxesOptions}
                value={from}
                onChange={setFrom}
                disabled={isSubmitting}
              />
            </Box>
          )}
          <MailReceivers
            disabled={isSubmitting}
            initialReceivers={initialReceivers}
            onChangeReceivers={setReceivers}
            onChangeCcReceivers={setCcReceivers}
            onChangeBccReceivers={setBccReceivers}
          />

          <Box sx={styles.subjectWrapper}>
            <Label>Subject</Label>
            <InputFieldV2
              label=""
              type="text"
              fullWidth
              sx={styles.subject}
              value={subject}
              onChange={setSubject}
              disabled={isSubmitting}
            />
          </Box>

          <Box sx={styles.emailContainer}>
            <Box>
              <ReactQuill
                ref={quillRef}
                value={editorValue}
                modules={quillModules}
                onChange={handleTextChange}
                readOnly={isSubmitting}
                disabled={isSubmitting}
                className={`compose-quill ${
                  isSubmitting ? 'quill-disabled' : ''
                }`}
              />
              <Box
                sx={{
                  ...styles.signatureWrapper,
                  opacity: isSubmitting ? 0.5 : 1,
                }}
                dangerouslySetInnerHTML={{
                  // eslint-disable-line react/no-danger
                  __html: signature,
                }}
              />
              <EmailReplySignatures
                teams={teams}
                agent={agent}
                isOpen={signaturePopoverOpen}
                onClose={() => setSignaturePopoverOpen(false)}
                onChange={handleChangeSignature}
              />
            </Box>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={allowedFileTypes.accept}
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />

            {attachments.length > 0 && (
              <AttachmentList
                attachments={attachments}
                disabled={isSubmitting}
                onDelete={handleRemoveAttachment}
              />
            )}
          </Box>
        </Box>
        <Box sx={styles.footer}>
          <ButtonV2
            size="small"
            onClick={handleSend}
            loading={isSubmitting}
            disabled={isSubmitting || receivers.length === 0}
          >
            Send
          </ButtonV2>
          {onDelete && (
            <ButtonV2
              size="small"
              color="error"
              variant="text"
              onClick={handleDelete}
              disabled={isSubmitting}
            >
              <DeleteIcon /> Delete
            </ButtonV2>
          )}
        </Box>
      </Box>
      {isTemplatesModalOpen && (
        <EmailTemplatesModal
          isOpen={isTemplatesModalOpen}
          onClose={handleCloseTemplatesModal}
          onSubmit={handleInsertTemplateSubmit}
        />
      )}
    </ModalFormSection>
  );
};

export default SendMailFields;
